import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import PageLoader from "modules/shared/components/PageLoader";
import NotFound from "modules/shared/components/NotFound";
import { useProjectDetailsStore } from "modules/organization/store";
import { useRankingProjectListStore } from "modules/organization/store/rankingProjectListStore";

// Ranking-project-scoped counterpart to ProjectOutlet.tsx (projectOutlet/ProjectOutlet.tsx):
// resolves common Ranking Project details (title, ranking type, dates, etc.) once per
// `ranking_project_id`, guards loading/not-found, then hands off to whichever ranking-project page
// (equation editor, snapshot, ...) is nested under it via <Outlet/>.
//
// Deliberately reuses `rankingProjectListStore` (already populated by the Ranking Projects list
// page's card list) instead of a dedicated fetch — there's no "get a single ranking project by
// id" endpoint, only a list one, and the list page has usually already loaded it by the time a
// user drills into a specific ranking project. Only refetches when the cached list is missing or
// belongs to a different project, so navigating list -> equation editor/snapshot doesn't cost a
// second identical get-ranking-projects call on top of the list page's own.
const RankingProjectOutlet = () => {
  const { ranking_project_id } = useParams<{ ranking_project_id: string }>();
  const { projectDetails } = useProjectDetailsStore();
  const { rankingProjects, currentProjectSlug, refreshRankingProjectList } =
    useRankingProjectListStore();

  const rankingProjectId = Number(ranking_project_id);
  const isValidId = !!ranking_project_id && !Number.isNaN(rankingProjectId);
  const isCachedForThisProject =
    !!rankingProjects && currentProjectSlug === projectDetails?.slug;

  useEffect(() => {
    if (!projectDetails?.slug || !isValidId || isCachedForThisProject) {
      return;
    }
    refreshRankingProjectList(projectDetails.slug).catch((error) =>
      console.log(error),
    );
  }, [
    projectDetails?.slug,
    isValidId,
    isCachedForThisProject,
    refreshRankingProjectList,
  ]);

  if (!isValidId) {
    return <NotFound />;
  }

  if (!isCachedForThisProject) {
    return <PageLoader />;
  }

  const rankingProjectDetails = rankingProjects?.find(
    (project) => project.id === rankingProjectId,
  );

  if (!rankingProjectDetails) {
    return <NotFound />;
  }

  return <Outlet />;
};

export default RankingProjectOutlet;
